import { formatSpan } from '../lifetime';
import { formatValue } from './format';
import { readout, startValue } from './panelData';
import type { ChartSeries, Unit } from './seriesTypes';

// What a value did over a range, in the units the design settled on:
//   probability  percentage points (20.5 % -> 27 % is +6.5 pts; relative
//                percent is misleading at small probabilities)
//   price        percent, with the absolute amount beside it
// Pure: the values strip, the single-outcome header and the asset header all
// read their numbers from here.

export type ChangeUnit = 'probability' | 'price';
export type ChangeDirection = 'up' | 'down' | 'flat';

export interface Change {
    unit: ChangeUnit;
    direction: ChangeDirection;
    /** Percentage points for a probability, percent for a price. Signed. */
    primary: number;
    /** A price only: the difference itself, in the price's currency. Signed. */
    absolute: number | null;
    /**
     * A price that moved, but by less than the 0.1% the percent prints with:
     * it reads "<0.1%" with its sign instead of a "0.0%" beside an amount.
     */
    tiny: boolean;
}

const MINUS = '−';

export function changeOverRange(
    unit: Unit,
    start: number | null,
    end: number | null
): Change | null {
    if (unit !== 'probability' && unit !== 'price') return null;
    if (start === null || end === null) return null;
    if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
    let primary: number;
    let absolute: number | null = null;
    if (unit === 'probability') {
        primary = (end - start) * 100;
    } else {
        if (start === 0) return null;
        primary = ((end - start) / Math.abs(start)) * 100;
        absolute = end - start;
    }
    // Flat means "prints as 0.0": decided on the rounded number, so the sign
    // and the digits can never disagree. A price that moved by less than that
    // is not flat, it is tiny: its amount says which way.
    const prints0 = Number(Math.abs(primary).toFixed(1)) === 0;
    const tiny = prints0 && absolute !== null && absolute !== 0;
    const direction: ChangeDirection = tiny
        ? (absolute as number) > 0
            ? 'up'
            : 'down'
        : prints0
          ? 'flat'
          : primary > 0
            ? 'up'
            : 'down';
    return { unit, direction, primary, absolute, tiny };
}

/** The change of a series between t0 and t1 (or the given end value). */
export function seriesChange(
    series: ChartSeries,
    t0: number,
    t1: number,
    end?: number | null
): Change | null {
    return changeOverRange(
        series.unit,
        startValue(series, t0),
        end === undefined ? readout(series, t1) : end
    );
}

const signOf = (direction: ChangeDirection): string =>
    direction === 'up' ? '+' : direction === 'down' ? MINUS : '';

/** "+6.5": the bare number the values strip prints under a caption naming the unit. */
export function formatChangeNumber(change: Change): string {
    const digits = change.tiny ? '<0.1' : Math.abs(change.primary).toFixed(1);
    return `${signOf(change.direction)}${digits}`;
}

/** "+6.5 pts" for a probability, "+2.3%" for a price. */
export function formatChange(change: Change): string {
    return `${formatChangeNumber(change)}${
        change.unit === 'probability' ? ' pts' : '%'
    }`;
}

/** "+$2.78": the amount beside a price's percent; null for a probability. */
export function formatAbsolute(change: Change): string | null {
    if (change.absolute === null || change.absolute === 0) return null;
    const sign = change.absolute > 0 ? '+' : change.absolute < 0 ? MINUS : '';
    return `${sign}${formatValue('price', Math.abs(change.absolute))}`;
}

export function changeArrow(change: Change): string {
    return change.direction === 'up'
        ? '▲'
        : change.direction === 'down'
          ? '▼'
          : '';
}

export function changeColor(
    change: Change | null
): 'success.main' | 'error.main' | 'text.secondary' {
    if (change?.direction === 'up') return 'success.main';
    if (change?.direction === 'down') return 'error.main';
    return 'text.secondary';
}

/** The range as it reads inside a sentence: "1D", but "all time" for All. */
export function spanPhrase(label: string): string {
    return label === 'All' ? 'all time' : label;
}

/** The pressed range button's label, else the length of the shown range ("5d", "3h"). */
export function rangeLabelOf(
    pressedLabel: string | null | undefined,
    t0: number,
    t1: number
): string {
    return pressedLabel ?? formatSpan(t1 - t0);
}
